/**
 * 82. Shipments — shipment status; carrier; tracking; delivery proof.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";

interface ShipmentRow {
  id: string;
  shipmentNumber: string;
  carrier: string;
  trackingNumber: string | null;
  shipDate: string | null;
  etaDate: string | null;
  destination: string;
  itemCount: number;
  status:
    | "Pending"
    | "Labeled"
    | "PickedUp"
    | "InTransit"
    | "Delivered"
    | "Exception";
}

export function ShipmentsPage() {
  const { data, isLoading } = useApiList<ShipmentRow>(
    ["logistics", "shipments"],
    "/logistics/shipments",
  );

  return (
    <div>
      <PageHeader
        title="Shipments"
        subtitle="Shipment status, carrier, tracking, and delivery proof."
        actions={<Button variant="primary">New shipment</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Shipments (${data.total})`}>
          <DataTable<ShipmentRow>
            rows={data.items}
            columns={[
              {
                key: "shipmentNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.shipmentNumber}</span>
                ),
              },
              { key: "carrier", label: "Carrier" },
              {
                key: "trackingNumber",
                label: "Tracking",
                render: (r) =>
                  r.trackingNumber ? (
                    <span className="atlas-mono">{r.trackingNumber}</span>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "shipDate",
                label: "Shipped",
                render: (r) => (r.shipDate ? formatDate(r.shipDate) : "—"),
              },
              {
                key: "etaDate",
                label: "ETA",
                render: (r) => (r.etaDate ? formatDate(r.etaDate) : "—"),
              },
              { key: "destination", label: "Destination" },
              { key: "itemCount", label: "Items", numeric: true },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Delivered" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Exception" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "InTransit" ? (
                    <Badge tone="primary">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
